package com.schoolconnect.app.repository.role

import com.schoolconnect.app.datasource.APP_STORAGE_PREFIX
import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.RoleResponse
import com.schoolconnect.app.util.scLog
import com.russhwolf.settings.Settings
import kotlin.time.Clock
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

object RoleLookupRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] RoleLookupRepository"
    private const val PAGE_SIZE = 50
    private const val MAX_PAGES = 20
    private const val MAX_MEMORY_CACHE_ENTRIES = 32
    private const val CACHE_PREFIX = "${APP_STORAGE_PREFIX}role_lookup_"

    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
        explicitNulls = false
    }

    private val settingsOrNull: Settings? by lazy(LazyThreadSafetyMode.SYNCHRONIZED) {
        runCatching { Settings() }.getOrNull()
    }

    private val memoryCache = LinkedHashMap<String, RoleCatalogSnapshot>()
    private var schoolAdminRolesCache: List<RoleResponse>? = null

    @Serializable
    private data class RoleCatalogSnapshot(
        val roles: List<RoleResponse> = emptyList(),
        val fetchedAtEpochMs: Long = 0L
    )

    private fun scopeKey(schoolId: String?, includeGlobal: Boolean): String {
        val normalizedSchoolId = schoolId?.trim().orEmpty().ifBlank { "global" }
        val safeSchoolId = normalizedSchoolId.replace(Regex("[^A-Za-z0-9._-]"), "_")
        return "${safeSchoolId.lowercase()}_${if (includeGlobal) "with_global" else "tenant_only"}"
    }

    private fun storageKey(scopeKey: String): String = "$CACHE_PREFIX$scopeKey"

    fun loadCachedRoles(
        schoolId: String? = null,
        includeGlobal: Boolean = true
    ): List<RoleResponse> {
        val scopeKey = scopeKey(schoolId, includeGlobal)
        memoryCache.remove(scopeKey)?.let {
            memoryCache[scopeKey] = it
            return it.roles
        }

        val snapshot = readSnapshot(scopeKey) ?: return emptyList()
        memoryCache[scopeKey] = snapshot
        return snapshot.roles
    }

    fun getRoleName(
        roleId: Int?,
        schoolId: String? = null,
        includeGlobal: Boolean = true
    ): String? {
        val id = roleId ?: return null
        return loadCachedRoles(schoolId, includeGlobal).firstOrNull { it.id == id }?.roleName
    }

    fun getRoleId(
        roleName: String?,
        schoolId: String? = null,
        includeGlobal: Boolean = true
    ): Int? {
        val normalizedName = roleName?.trim().orEmpty()
        if (normalizedName.isBlank()) return null
        return loadCachedRoles(schoolId, includeGlobal).firstOrNull {
            it.roleName.equals(normalizedName, ignoreCase = true)
        }?.id
    }

    suspend fun refreshRolesFromRemote(
        schoolId: String? = null,
        includeGlobal: Boolean = true,
        forceRemote: Boolean = true
    ): Result<List<RoleResponse>> {
        val normalizedSchoolId = schoolId?.trim()?.takeIf { it.isNotBlank() }
        val scopeKey = scopeKey(normalizedSchoolId, includeGlobal)
        if (!forceRemote) {
            val cachedRoles = loadCachedRoles(normalizedSchoolId, includeGlobal)
            if (cachedRoles.isNotEmpty()) {
                scLog("$TAG refreshRolesFromRemote cacheHit scope=$scopeKey count=${cachedRoles.size}")
                return Result.success(cachedRoles)
            }
        }

        scLog("$TAG refreshRolesFromRemote start scope=$scopeKey school=${normalizedSchoolId ?: "<global>"} includeGlobal=$includeGlobal")
        return runCatching {
            val roles = mutableListOf<RoleResponse>()
            var offset = 0
            var pageCount = 0

            while (pageCount < MAX_PAGES) {
                val page = KtorApiClient.getRoles(
                    schoolId = normalizedSchoolId,
                    includeGlobal = includeGlobal,
                    limit = PAGE_SIZE,
                    offset = offset
                ).getOrElse { throw it }

                scLog("$TAG refreshRolesFromRemote page scope=$scopeKey offset=$offset count=${page.size}")
                roles += page
                pageCount += 1

                if (page.size < PAGE_SIZE) {
                    break
                }
                offset += PAGE_SIZE
            }

            val snapshot = RoleCatalogSnapshot(
                roles = roles.toList(),
                fetchedAtEpochMs = Clock.System.now().toEpochMilliseconds()
            )
            persistSnapshot(scopeKey, snapshot)
            scLog("$TAG refreshRolesFromRemote complete scope=$scopeKey total=${roles.size} pages=$pageCount")
            roles.toList()
        }.onFailure { throwable ->
            scLog("$TAG refreshRolesFromRemote failure scope=$scopeKey", throwable)
        }
    }

    suspend fun refreshSchoolAdminRoles(forceRemote: Boolean = false): Result<List<RoleResponse>> {
        schoolAdminRolesCache?.takeIf { it.isNotEmpty() && !forceRemote }?.let {
            scLog("$TAG refreshSchoolAdminRoles cacheHit count=${it.size}")
            return Result.success(it)
        }

        scLog("$TAG refreshSchoolAdminRoles start")
        return runCatching {
            val roles = mutableListOf<RoleResponse>()
            var offset = 0
            var pageCount = 0
            while (pageCount < MAX_PAGES) {
                val page = KtorApiClient.getSchoolAdminRoles(PAGE_SIZE, offset).getOrElse { throw it }
                roles += page
                pageCount += 1
                scLog("$TAG refreshSchoolAdminRoles page offset=$offset count=${page.size}")
                if (page.size < PAGE_SIZE) break
                offset += PAGE_SIZE
            }
            roles.toList().also {
                schoolAdminRolesCache = it
                scLog("$TAG refreshSchoolAdminRoles success count=${it.size} pages=$pageCount")
            }
        }.onFailure { throwable ->
            scLog("$TAG refreshSchoolAdminRoles failure", throwable)
        }
    }

    private fun persistSnapshot(scopeKey: String, snapshot: RoleCatalogSnapshot) {
        if (memoryCache.size >= MAX_MEMORY_CACHE_ENTRIES) {
            memoryCache.entries.firstOrNull()?.key?.let(memoryCache::remove)
        }
        memoryCache[scopeKey] = snapshot
        settingsOrNull?.putString(storageKey(scopeKey), json.encodeToString(snapshot))
    }

    fun clearMemoryCache() {
        memoryCache.clear()
        schoolAdminRolesCache = null
    }

    private fun readSnapshot(scopeKey: String): RoleCatalogSnapshot? {
        val encoded = settingsOrNull?.getString(storageKey(scopeKey), "").orEmpty()
        if (encoded.isBlank()) return null
        return runCatching {
            json.decodeFromString<RoleCatalogSnapshot>(encoded)
        }.getOrNull()
    }
}
